import { icebreakerCopy, subtitleCopy } from "../../iris/maker-language";
import { CastPicker } from "../CastPicker";
import type { TalkActions, TalkMessage, TalkView } from "../IntakeTalk";
import { PhotoOffer } from "../PhotoOffer";
import { QuickReplies } from "../QuickReplies";
import type { SubtitleScreen } from "../subtitle-view";

/** The three directions as cards: only when no board is beside the chat to hold them. */
function Directions({ message, index, talk, busy }: { message: TalkMessage; index: number; talk: TalkActions; busy: boolean }) {
  return (
    <div className="talk-concepts" role="group" aria-label="Three directions for your film">
      {(message.concepts ?? []).map((concept, conceptIndex) => {
        const chosen = message.chosen === conceptIndex;
        const decided = message.chosen !== undefined;
        return (
          <article
            key={concept.name}
            className={`talk-concept${chosen ? " chosen" : ""}${decided && !chosen ? " faded" : ""}`}
            style={{ animationDelay: `${conceptIndex * 120}ms` }}
          >
            <span className="talk-concept-number">{conceptIndex + 1}</span>
            <h5>{concept.name}</h5>
            <p className="talk-concept-logline">{concept.logline}</p>
            {/* Folded by default, so three directions stay readable in a small window. */}
            <details className="talk-concept-more">
              <summary>Why this fits</summary>
            <p className="talk-concept-why">{concept.why}</p>
            <dl>
              {concept.mood && (
                <>
                  <dt>Mood</dt>
                  <dd>{concept.mood}</dd>
                </>
              )}
              {(concept.places.length > 0 || concept.world) && (
                <>
                  <dt>World</dt>
                  <dd>{concept.places.length ? concept.places.map((place) => place.name).join(" → ") : concept.world}</dd>
                </>
              )}
              {concept.cast_idea && (
                <>
                  <dt>Cast</dt>
                  <dd>{concept.cast_idea}</dd>
                </>
              )}
            </dl>
            </details>
            <button
              type="button"
              className="talk-concept-choose"
              disabled={busy || decided || !talk.chooseConcept}
              onClick={() => talk.chooseConcept?.(index, conceptIndex)}
            >
              {chosen ? "✓ Chosen" : "Choose this direction"}
            </button>
          </article>
        );
      })}
      {message.chosen === undefined && talk.moreConcepts && (
        <button type="button" className="talk-concepts-more" disabled={busy} onClick={talk.moreConcepts}>
          Three other directions
        </button>
      )}
    </div>
  );
}

/** Under the subtitle there is one choice at most: Try again, one sheet, or the quick replies. */
export function ChoiceSheet({ screen, view, talk, busy, revealing }: {
  screen: SubtitleScreen;
  view: TalkView;
  talk: TalkActions;
  busy: boolean;
  revealing: boolean;
}) {
  if (revealing) return null;
  if (screen.current?.error && talk.retry)
    return (
      <div className="subtitle-choice">
        <button type="button" className="quick-chip on" disabled={busy} onClick={talk.retry}>{subtitleCopy().retry}</button>
      </div>
    );
  const sheet = screen.sheet;
  if (sheet) {
    const message = view.messages[sheet.index]!;
    if ((sheet.kind === "cast" || sheet.kind === "wardrobe") && view.castOptions?.length && talk.pickCast)
      return (
        <div className="subtitle-choice sheet">
          <CastPicker options={view.castOptions} mode={sheet.kind} initial={view.castPick} disabled={busy}
            onPick={(pick) => talk.pickCast?.(sheet.index, pick, sheet.kind as "cast" | "wardrobe")} />
        </div>
      );
    if (sheet.kind === "photo_session" && !view.photoChosen && talk.photoReady)
      return (
        <div className="subtitle-choice sheet">
          <PhotoOffer open castOptions={view.castOptions} disabled={busy} onReady={talk.photoReady} />
        </div>
      );
    if (sheet.kind === "directions")
      return (
        <div className="subtitle-choice sheet">
          <Directions message={message} index={sheet.index} talk={talk} busy={busy} />
        </div>
      );
    return null;
  }
  const chips = screen.chips;
  if (!chips) return null;
  return (
    <div className="subtitle-choice">
      <QuickReplies options={chips.options} multi={chips.multi} extras={chips.extras} disabled={busy} continueLabel={icebreakerCopy().continue}
        onAnswer={(text) => talk.send(text)} onExtra={(id) => talk.quickExtra?.(chips.index, id)} />
    </div>
  );
}
